import { ModuleId } from '../types';

export interface LessonContent {
    moduleId: ModuleId;
    title: string;
    sections: {
        heading: string;
        content: string; // Markdown-like text
        keyTerms?: string[];
    }[];
}

export const LESSONS: Record<string, LessonContent> = {
    'inventory-logic': {
        moduleId: 'inventory-logic',
        title: 'Algorithms in the Real World',
        sections: [
            {
                heading: "What is an Algorithm?",
                content: "An algorithm is simply a set of specific steps to solve a problem. It's like a recipe: 1. Preheat oven, 2. Mix flour, 3. Bake. In coding, we write algorithms to tell the computer exactly what to do in different situations.",
                keyTerms: ["Algorithm", "Logic", "Conditional"]
            },
            {
                heading: "The 'If/Else' Decision Tree",
                content: "Computers make decisions using 'conditionals'. We ask a question (Is stock 0?), and if the answer is YES, we do one thing. If NO, we do something else. This branching logic is the foundation of all software intelligence.",
                keyTerms: ["Branched Logic", "Control Flow"]
            },
            {
                heading: "Real World Application",
                content: "Amazon and Walmart use this exact logic but at a massive scale. Their systems check millions of products every second. When the 'Low Stock' branch is triggered, they don't just alert a human—they might automatically send a Purchase Order to a supplier!"
            }
        ]
    },
    'react-components': {
        moduleId: 'react-components',
        title: 'Thinking in Components',
        sections: [
            {
                heading: "The Lego Brick Philosophy",
                content: "Before modern frameworks like React, websites were often one giant file of HTML. This was hard to manage. React introduced 'Component-Based Architecture'. Imagine building a Lego castle: you don't mold the plastic for the whole castle at once. You build small, reusable bricks.",
                keyTerms: ["Component", "Reusability", "Modular"]
            },
            {
                heading: "Don't Repeat Yourself (DRY)",
                content: "The DRY principle is a golden rule in coding. If you copy-paste the same code 3 times, you should probably turn it into a Component. That way, if you need to fix a bug, you fix it in one place (the component definition) and it updates everywhere."
            },
            {
                heading: "Composition",
                content: "Websites are just components inside other components. A 'Page' component contains a 'Header' and a 'Body'. The 'Header' contains a 'Logo' and a 'Menu'. This nesting is called Composition."
            }
        ]
    },
    'react-state': {
        moduleId: 'react-state',
        title: 'State & Memory',
        sections: [
            {
                heading: "Why do we need State?",
                content: "Standard web pages are 'stateless'—they don't remember anything. If you refresh, the data is gone. 'State' is a special javascript object that allows a Component to 'remember' information (like the number of bananas in a cart) while the user interacts with it.",
                keyTerms: ["State", "Persistence", "Memory"]
            },
            {
                heading: "One-Way Data Flow",
                content: "In React, data flows down like a waterfall. A parent component passes data to children. But children rarely pass data back up directly. This strict rule makes debugging easier because you always know where the data is coming from."
            },
            {
                heading: "Immutability",
                content: "We never change state directly (e.g., state = 5). We use a function (setState) to ask React to update it. This ensures React knows a change happened and can update the screen (Render) immediately."
            }
        ]
    },
    'react-props': {
        moduleId: 'react-props',
        title: 'Data River',
        sections: [
            {
                heading: "What are Props?",
                content: "Props (short for Properties) are how components talk to each other. Think of them like inputs to a function. A parent component passes data DOWN to a child component via attributes.",
                keyTerms: ["Props", "Parent", "Child"]
            },
            {
                heading: "One-Way Data Flow",
                content: "Data in React flows like a waterfall: ALWAYS from Top (Parent) to Bottom (Child). A child cannot pass data back up directly; it must use a callback function provided by the parent.",
                keyTerms: ["Unidirectional", "Drilling"]
            },
            {
                heading: "The Read-Only Rule",
                content: "A component can READ its props, but it must never CHANGE them. Props are immutable. If a component needs to change something, it should use State instead."
            }
        ]
    },
    'game-polish': {
        moduleId: 'game-polish',
        title: 'The Art of Game Juice',
        sections: [
            {
                heading: "What is Juice?",
                content: "\"Juice\" is the non-essential feedback that makes a game feel alive. It's the squish when you jump, the flash when you shoot, and the shake when you get hit. Without juice, a game feels like a spreadsheet.",
                keyTerms: ["Juice", "Feedback", "Polish"]
            },
            {
                heading: "Particle Systems",
                content: "Particles are tiny, temporary images used to create effects like fire, smoke, dust, or magic. They spawn, move, and die quickly. Using them generously is the easiest way to make an action feel impactful."
            },
            {
                heading: "Screenshake",
                content: "When a heavy object hits the ground, the earth shakes. We mimic this in games by vibrating the camera view. Use it sparingly! Too much screenshake can make players motion sick.",
                keyTerms: ["Screenshake", "Impact"]
            }
        ]
    },
    'boss-logic': {
        moduleId: 'boss-logic',
        title: 'State Machines (The Boss)',
        sections: [
            {
                heading: "What is a State Machine?",
                content: "A Finite State Machine (FSM) is a model of behavior where an entity can only be in one \"State\" at a time (e.g., Idle, Attack, Hurt). It switches states based on \"Triggers\" (e.g., Timer finished, Player close).",
                keyTerms: ["State Machine", "FSM", "Transition"]
            },
            {
                heading: "Boss Patterns",
                content: "Bosses aren't smart; they just follow a list. 1. Wait (Idle), 2. Shoot (Attack), 3. Get tired (Vulnerable). By randomizing the order or duration of these states, we create the illusion of intelligence.",
                keyTerms: ["Pattern", "Cycle", "RNG"]
            },
            {
                heading: "Collision & HP",
                content: "When the player attacks, we check if the Boss is in a \"Vulnerable\" state. If yes, -HP. If no (Blocking), no damage. This simple conditional logic creates the strategy of the fight."
            }
        ]
    }
};
